import * as Sentry from '@sentry/svelte';

const MAX_REPORTED_SIBLING_KEYS = 20;

const sentryReporter = {
  breadcrumb: (message, data) => Sentry.addBreadcrumb({ category: 'storage', level: 'warning', message, data }),
  report: (message, data) =>
    Sentry.captureMessage(message, {
      level: 'warning',
      tags: { storage_state: data.storage_state },
      extra: data
    })
};

export class LocalStorageDB {
  constructor(key, reporter = sentryReporter) {
    if (!key) {
      throw new Error('No parameter key passed to constructor. Expected String but got undefined');
    }

    this.STORE_KEY = key;
    this.store = window.localStorage;
    this.reporter = reporter;
    this.reported = false;
    this.createdAt = Date.now();

    const isStorageCreated = this.store.getItem(this.STORE_KEY);

    if (!isStorageCreated) {
      this.store.setItem(this.STORE_KEY, JSON.stringify({}));
    }
  }

  // The browser can drop the store key mid-session (policy wipes, eviction). Reads used
  // to hand back null from here, so every caller threw and took the render loop with it.
  read() {
    const raw = this.store.getItem(this.STORE_KEY);

    let storeData = null;

    try {
      storeData = JSON.parse(raw);
    } catch {
      storeData = null;
    }

    if (storeData && typeof storeData === 'object') {
      return storeData;
    }

    this.report(raw === null ? 'missing' : 'malformed');
    this.store.setItem(this.STORE_KEY, JSON.stringify({}));

    return {};
  }

  // Key names only, never values — this is diagnostic context for an unexplained wipe.
  report(state) {
    const data = {
      storage_state: state,
      session_age_seconds: Math.round((Date.now() - this.createdAt) / 1000),
      visibility: typeof document === 'undefined' ? 'unknown' : document.visibilityState,
      sibling_keys: Object.keys(this.store)
        .filter((key) => key !== this.STORE_KEY)
        .slice(0, MAX_REPORTED_SIBLING_KEYS)
    };

    const message = `localStorage "${this.STORE_KEY}" store ${state}`;

    this.reporter.breadcrumb(message, data);

    // One issue per page load is enough to diagnose; the breadcrumbs carry the rest.
    if (this.reported) {
      return;
    }

    this.reported = true;
    this.reporter.report(message, data);
  }

  create(key, data) {
    const storeData = this.read();
    const existingData = storeData[key];

    if (existingData) {
      if (existingData instanceof Array) {
        existingData.push(data);
        Object.assign(storeData[key], existingData);
      } else {
        storeData[key] = [existingData];
        storeData[key].push(data);
        this.store.setItem(this.STORE_KEY, JSON.stringify(storeData));
      }
    } else {
      const newData = {};
      newData[key] = data;
      Object.assign(storeData, newData);
    }

    this.store.setItem(this.STORE_KEY, JSON.stringify(storeData));
  }

  get(key = null, pos = null) {
    const storeData = this.read();

    if (pos === null && key === null) {
      return storeData;
    } else if (key && !pos) {
      return storeData[key];
    }

    return storeData[key]?.[pos];
  }

  update(key, update, pos = null) {
    const storeData = this.read();

    if (pos === null) {
      storeData[key] = update;
    } else {
      // A recovered store has no entry to write into yet.
      if (!storeData[key] || typeof storeData[key] !== 'object') {
        storeData[key] = {};
      }

      storeData[key][pos] = update;
    }

    this.store.setItem(this.STORE_KEY, JSON.stringify(storeData));
  }

  remove(key, pos = null) {
    let storeData = this.read();

    if (!key) {
      storeData = {};
    }

    if (pos === null) {
      delete storeData[key];
    } else if (storeData[key] instanceof Array) {
      storeData[key].splice(pos, 1);
    } else if (storeData[key]) {
      delete storeData[key][pos];
    }

    this.store.setItem(this.STORE_KEY, JSON.stringify(storeData));

    return true;
  }
}

export const LocalStorage = new LocalStorageDB('opensense');
